import { useMemo } from "react";
import { useSelector } from "react-redux";
import { getReviewPendingNavEntriesForFacility } from "../../../constants/reviewPendingNavConfig";
import { AlgoliaFacetAttribute } from "../../../enums/worklistEnums";
import { RootState } from "../../../redux/store/store";

/**
 * Fetches Review Pending counts for facility-allowed entries only
 * (Firebase: review_pending_master_auth_statuses).
 *
 * @returns Object containing:
 *   - countsBySidebarId: Record mapping sidebar IDs (e.g. "review-pending/no-auth-required") to order counts
 *   - loading: Whether the Postgres facet results are still loading
 */

export const useReviewPendingCount = () => {
  const {
    is_facet_results_loading: isPostgresFacetResultsLoading,
    facet_results: postgresFacetResults,
  } = useSelector((state: RootState) => state.postgresFacetCounts);

  const { data: singleHealthFacilityDetails } = useSelector(
    (state: RootState) => state.singleHealthFacilityDetails,
  );

  const reviewPendingEntries = useMemo(
    () => getReviewPendingNavEntriesForFacility(singleHealthFacilityDetails),
    [singleHealthFacilityDetails],
  );

  const countsByMasterAuthStatus = useMemo(() => {
    return (
      postgresFacetResults?.facet_results?.[
        AlgoliaFacetAttribute.MasterAuthStatus
      ] ?? {}
    );
  }, [postgresFacetResults]);

  const countsBySidebarId = useMemo(() => {
    const map: Record<string, number> = {};
    for (const entry of reviewPendingEntries) {
      map[entry.id] = countsByMasterAuthStatus[entry.masterAuthStatus] ?? 0;
    }
    return map;
  }, [countsByMasterAuthStatus, reviewPendingEntries]);

  return {
    countsBySidebarId,
    loading: isPostgresFacetResultsLoading,
  };
};
